import { useEffect, useMemo, useState } from 'react';
import {
  FINGER_COLORS,
  FINGER_NAMES,
  fingerNameForChar,
  getKeyboardLayout,
  isCharUnlocked,
  resolveExpectedKey,
} from '../data/keyboardLayouts';
import type { KeyDef, KeyboardLayoutId } from '../types';

export type HintLevel = 'none' | 'soft' | 'strong';

interface KeyboardProps {
  layoutId: KeyboardLayoutId;
  nextExpectedKey?: string;
  lastPressedKey?: string;
  lastPressCorrect?: boolean;
  unlockedKeys: ReadonlyArray<string>;
  /** Intro preview: highlight these keys (ignores nextExpectedKey). */
  highlightedKeys?: ReadonlyArray<string>;
  showFingerDot?: boolean;
  hintLevel: HintLevel;
}

export default function Keyboard({
  layoutId,
  nextExpectedKey,
  lastPressedKey,
  lastPressCorrect,
  unlockedKeys,
  highlightedKeys,
  showFingerDot = true,
  hintLevel,
}: KeyboardProps) {
  const layout = useMemo(() => getKeyboardLayout(layoutId), [layoutId]);
  const [flash, setFlash] = useState<
    { key: string; correct: boolean } | undefined
  >(undefined);

  useEffect(() => {
    if (!lastPressedKey) return;
    setFlash({
      key: lastPressedKey,
      correct: lastPressCorrect !== false,
    });
    const t = window.setTimeout(() => setFlash(undefined), 300);
    return () => window.clearTimeout(t);
  }, [lastPressedKey, lastPressCorrect]);

  const resolved = nextExpectedKey
    ? resolveExpectedKey(nextExpectedKey, layoutId)
    : undefined;
  const highlightSet = highlightedKeys
    ? new Set(highlightedKeys.map((k) => (k === ' ' ? ' ' : k.toLowerCase())))
    : null;
  const expectedBase = highlightSet ? undefined : resolved?.baseChar;
  const needsShift = !highlightSet && (resolved?.needsShift ?? false);

  return (
    <div className="w-full">
      {needsShift && hintLevel !== 'none' && (
        <p className="mb-2 text-center text-xs font-medium text-amber-800 dark:text-amber-200">
          Hold Shift og tryk den markerede tast
        </p>
      )}
      <div className="mx-auto flex max-w-4xl flex-col gap-1 surface p-3">
        {layout.map((row, ri) => (
          <div key={ri} className="flex gap-1">
            {row.map((k, ki) => {
              const isShiftKey =
                !k.decorative && k.label === 'Shift' && needsShift;
              const matchesExpected =
                !!expectedBase &&
                !k.decorative &&
                k.char === expectedBase;
              const capHint =
                highlightSet && !k.decorative && highlightSet.has(k.char)
                  ? 'strong'
                  : matchesExpected || isShiftKey
                    ? hintLevel
                    : 'none';

              return (
                <KeyCap
                  key={ki}
                  keyDef={k}
                  hintLevel={capHint}
                  flash={
                    flash &&
                    !k.decorative &&
                    (k.char === flash.key.toLowerCase() ||
                      k.shift === flash.key)
                      ? flash.correct
                        ? 'correct'
                        : 'wrong'
                      : undefined
                  }
                  isUnlocked={
                    k.decorative
                      ? true
                      : isCharUnlocked(k.char, unlockedKeys) ||
                        (k.shift ? isCharUnlocked(k.shift, unlockedKeys) : false)
                  }
                  showFingerDot={
                    showFingerDot &&
                    (highlightSet
                      ? !k.decorative && highlightSet.has(k.char)
                      : capHint !== 'none')
                  }
                  shiftHint={isShiftKey}
                />
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

interface KeyCapProps {
  keyDef: KeyDef;
  hintLevel: HintLevel;
  flash: 'correct' | 'wrong' | undefined;
  isUnlocked: boolean;
  showFingerDot: boolean;
  shiftHint?: boolean;
}

function KeyCap({
  keyDef,
  hintLevel,
  flash,
  isUnlocked,
  showFingerDot,
  shiftHint,
}: KeyCapProps) {
  const width = keyDef.width ?? 1;
  const label =
    keyDef.label !== undefined
      ? keyDef.label
      : keyDef.char === ' '
        ? ''
        : keyDef.char;

  let stateClass = '';
  if (keyDef.decorative) {
    stateClass = shiftHint ? 'kc-expected-soft' : 'kc-decorative';
  } else if (hintLevel === 'strong') {
    stateClass = 'kc-expected-strong';
  } else if (hintLevel === 'soft') {
    stateClass = 'kc-expected-soft';
  } else if (isUnlocked) {
    stateClass = 'kc-unlocked';
  } else {
    stateClass = 'kc-locked';
  }

  if (flash === 'correct') stateClass += ' kc-flash-correct';
  if (flash === 'wrong') stateClass += ' kc-flash-wrong';

  return (
    <div
      className={`kc-base ${stateClass}`}
      style={{ flex: `${width} 1 0`, minWidth: 0 }}
      aria-label={label || keyDef.char || 'key'}
    >
      <span className="truncate px-1">
        {keyDef.char === ' ' ? '' : (label || '').toUpperCase()}
      </span>
      {showFingerDot && hintLevel !== 'none' && !keyDef.decorative && (
        <span
          className={`absolute -bottom-1 h-1.5 w-1.5 rounded-full ${FINGER_COLORS[keyDef.finger]}`}
        />
      )}
    </div>
  );
}

export function fingerNameForKey(
  nextExpectedKey: string,
  layoutId: KeyboardLayoutId,
): string | null {
  const r = resolveExpectedKey(nextExpectedKey, layoutId);
  if (r.needsShift) {
    const finger = r.def ? FINGER_NAMES[r.def.finger] : 'fingeren';
    return `Shift + ${finger}`;
  }
  return fingerNameForChar(nextExpectedKey, layoutId);
}
